import React, { createContext, useContext, useReducer, useCallback, useMemo, useRef } from 'react';
import { apiService } from '../services/api';
import cacheService from '../services/cacheService';

// Action types
const ORDER_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_ORDERS: 'SET_ORDERS',
  ADD_ORDER: 'ADD_ORDER',
  UPDATE_ORDER: 'UPDATE_ORDER',
  DELETE_ORDER: 'DELETE_ORDER',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_LAST_FETCH: 'SET_LAST_FETCH',
  INCREMENT_NEW_ORDERS: 'INCREMENT_NEW_ORDERS',
  RESET_NEW_ORDERS: 'RESET_NEW_ORDERS'
};

// Initial state
const initialState = {
  orders: [],
  loading: false,
  error: null,
  lastFetch: null,
  newOrdersCount: 0,
  cache: new Map(),
  cacheTimestamps: new Map()
};

// Reducer
const orderReducer = (state, action) => {
  switch (action.type) {
    case ORDER_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case ORDER_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    
    case ORDER_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };
    
    case ORDER_ACTIONS.SET_ORDERS:
      return { 
        ...state, 
        orders: action.payload, 
        loading: false, 
        error: null,
        lastFetch: new Date()
      };
    
    case ORDER_ACTIONS.ADD_ORDER:
      return { 
        ...state, 
        orders: [action.payload, ...state.orders],
        newOrdersCount: state.newOrdersCount + 1
      };
    
    case ORDER_ACTIONS.UPDATE_ORDER:
      return { 
        ...state, 
        orders: state.orders.map(order => 
          order._id === action.payload._id ? action.payload : order
        )
      };
    
    case ORDER_ACTIONS.DELETE_ORDER:
      return { 
        ...state, 
        orders: state.orders.filter(order => order._id !== action.payload)
      };
    
    case ORDER_ACTIONS.SET_LAST_FETCH:
      return { ...state, lastFetch: action.payload };
    
    case ORDER_ACTIONS.INCREMENT_NEW_ORDERS:
      return { ...state, newOrdersCount: state.newOrdersCount + action.payload };
    
    case ORDER_ACTIONS.RESET_NEW_ORDERS:
      return { ...state, newOrdersCount: 0 };
    
    default:
      return state;
  }
};

// Create context
const OrderContext = createContext();

// Custom hook for memoized cache
const useMemoizedCache = () => {
  const cache = useMemo(() => new Map(), []);
  const timestamps = useMemo(() => new Map(), []);
  
  const setCache = useCallback((key, data, ttl = 2 * 60 * 1000) => {
    const timestamp = Date.now();
    cache.set(key, data);
    timestamps.set(key, timestamp + ttl);
  }, [cache, timestamps]);
  
  const getCache = useCallback((key) => {
    const expiry = timestamps.get(key);
    if (!expiry || Date.now() > expiry) {
      cache.delete(key);
      timestamps.delete(key);
      return null;
    }
    return cache.get(key);
  }, [cache, timestamps]);
  
  const clearCache = useCallback((key) => {
    cache.delete(key);
    timestamps.delete(key);
  }, [cache, timestamps]);
  
  const clearAllCache = useCallback(() => {
    cache.clear();
    timestamps.clear();
  }, [cache, timestamps]);
  
  return { setCache, getCache, clearCache, clearAllCache };
};

// Provider component
export const OrderProvider = ({ children }) => {
  const [state, dispatch] = useReducer(orderReducer, initialState);
  const { setCache, getCache, clearCache, clearAllCache } = useMemoizedCache();
  
  // Rate limiting to prevent too many API calls
  const lastFetchTime = useRef(0);
  const FETCH_COOLDOWN = 5000; // 5 seconds minimum between fetches (increased to prevent resource exhaustion)
  const pendingFetch = useRef(null); // Track pending fetch to prevent duplicates
  const fetchCount = useRef(0); // Track number of fetches to prevent runaway

  // Memoized selectors
  const selectors = useMemo(() => ({
    getDraftOrders: () => state.orders.filter(order => order.status === 'Draft'),
    getUnderReviewOrders: () => state.orders.filter(order => order.status === 'Under Review'),
    getRelevantOrders: () => state.orders.filter(order => 
      order.status === 'Draft' || order.status === 'Under Review'
    ),
    getOrderById: (id) => state.orders.find(order => order._id === id),
    getOrdersByStatus: (status) => state.orders.filter(order => order.status === status),
    getOrdersByBranch: (branch) => state.orders.filter(order => 
      order.branch === branch || order.branchName === branch
    ),
    getOrdersByUser: (userName) => state.orders.filter(order => order.userName === userName),
    getOrdersByDateRange: (startDate, endDate) => state.orders.filter(order => {
      const orderDate = new Date(order.createdAt || order.dateTime);
      return orderDate >= startDate && orderDate <= endDate;
    })
  }), [state.orders]);

  // Fetch orders with intelligent caching and background updates
  const fetchOrders = useCallback(async (forceRefresh = false, useSubmissionEndpoint = false) => {
    // Circuit breaker - prevent runaway API calls
    if (fetchCount.current > 10 && !forceRefresh) {
      console.warn('Circuit breaker activated: Too many API calls, using cached data only');
      const cachedData = getCache(useSubmissionEndpoint ? 'orders_submission_data' : 'orders_data');
      if (cachedData) {
        dispatch({ type: ORDER_ACTIONS.SET_ORDERS, payload: cachedData });
        return cachedData;
      }
      return [];
    }

    // If there's already a pending fetch, return it instead of starting a new one
    if (pendingFetch.current && !forceRefresh) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Fetch already in progress, waiting for completion...');
      }
      return pendingFetch.current;
    }

    // Rate limiting - prevent too many API calls
    const now = Date.now();
    if (!forceRefresh && now - lastFetchTime.current < FETCH_COOLDOWN) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Rate limited: Too many API calls, using cached data');
      }
      const cachedData = getCache(useSubmissionEndpoint ? 'orders_submission_data' : 'orders_data');
      if (cachedData) {
        dispatch({ type: ORDER_ACTIONS.SET_ORDERS, payload: cachedData });
        return cachedData;
      }
    }
    lastFetchTime.current = now;
    fetchCount.current += 1;
    
    const cacheKey = useSubmissionEndpoint ? 'orders_submission_data' : 'orders_data';
    const endpoint = useSubmissionEndpoint ? '/orders/submission' : '/orders';
    
    // Check cache first if not forcing refresh
    if (!forceRefresh) {
      const cachedData = getCache(cacheKey);
      if (cachedData) {
        // Show cached data immediately
        dispatch({ type: ORDER_ACTIONS.SET_ORDERS, payload: cachedData });
        
        // Background fetch disabled to prevent infinite loops
        // Fresh data will be fetched on manual refresh only
        
        return cachedData;
      }
    }

    // No cache available or force refresh - show loading and fetch
    try {
      dispatch({ type: ORDER_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: ORDER_ACTIONS.CLEAR_ERROR });

      // Set pending fetch to prevent duplicate calls
      const fetchPromise = (async () => {
        let response;
        try {
          response = await apiService.get(endpoint);
        } catch (endpointError) {
          // Fallback to regular endpoint if submission endpoint fails
          if (useSubmissionEndpoint && endpointError.response?.status >= 500) {
            console.warn('Submission endpoint failed, falling back to regular endpoint');
            response = await apiService.get('/orders');
          } else {
            throw endpointError;
          }
        }
        let orders = response.data || [];
        
        // Debug logging
        if (process.env.NODE_ENV === 'development') {
          console.log('=== FETCH ORDERS DEBUG ===');
          console.log('Endpoint:', endpoint);
          console.log('Response data type:', typeof orders);
          console.log('Is array:', Array.isArray(orders));
          console.log('Has orders property:', orders && orders.orders);
          console.log('Response structure:', orders);
        }
        
        // Handle different response formats
        if (orders && orders.orders && Array.isArray(orders.orders)) {
          // New format with pagination: { orders: [...], pagination: {...} }
          orders = orders.orders;
          if (process.env.NODE_ENV === 'development') {
            console.log('Using paginated format, extracted orders:', orders.length);
          }
        } else if (!Array.isArray(orders)) {
          // Fallback to empty array if data is not an array
          console.warn('Orders data is not an array, using empty array:', orders);
          orders = [];
        }
        
        // Cache the data
        setCache(cacheKey, orders);
        
        // Ensure orders is an array before processing
        if (!Array.isArray(orders)) {
          console.error('Orders is not an array after processing:', orders);
          orders = [];
        }
        
        // Check for new orders
        const previousOrderIds = new Set(state.orders.map(o => o._id));
        const newOrders = orders.filter(o => !previousOrderIds.has(o._id));
        
        if (newOrders.length > 0 && state.orders.length > 0) {
          dispatch({ type: ORDER_ACTIONS.INCREMENT_NEW_ORDERS, payload: newOrders.length });
          
          // Show browser notification if permission is granted
          if (Notification.permission === 'granted') {
            new Notification(`New Order${newOrders.length > 1 ? 's' : ''} Available`, {
              body: `${newOrders.length} new order${newOrders.length > 1 ? 's' : ''} detected`,
              icon: '/favicon.ico'
            });
          }
        }
        
        dispatch({ type: ORDER_ACTIONS.SET_ORDERS, payload: orders });
        return orders;
      })();

      // Store the promise and clear it when done
      pendingFetch.current = fetchPromise;
      const result = await fetchPromise;
      pendingFetch.current = null;
      return result;
    } catch (error) {
      // Clear pending fetch on error
      pendingFetch.current = null;
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load orders';
      dispatch({ type: ORDER_ACTIONS.SET_ERROR, payload: errorMessage });
      throw error;
    }
  }, [getCache, setCache, state.orders]);

  // Create order
  const createOrder = useCallback(async (orderData) => {
    try {
      dispatch({ type: ORDER_ACTIONS.SET_LOADING, payload: true });
      
      const response = await apiService.orders.create(orderData);
      const newOrder = response.data;
      
      // Clear cache to ensure fresh data
      clearCache('orders_data');
      cacheService.clearOrdersData();
      
      // Add to state
      dispatch({ type: ORDER_ACTIONS.ADD_ORDER, payload: newOrder });
      
      return newOrder;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create order';
      dispatch({ type: ORDER_ACTIONS.SET_ERROR, payload: errorMessage });
      throw error;
    }
  }, [clearCache]);

  // Update order
  const updateOrder = useCallback(async (orderId, updateData) => {
    try {
      dispatch({ type: ORDER_ACTIONS.SET_LOADING, payload: true });
      
      const response = await apiService.orders.update(orderId, updateData);
      const updatedOrder = response.data;
      
      // Clear all caches to ensure fresh data
      clearCache('orders_data');
      clearCache('orders_submission_data');
      cacheService.clearOrdersData();
      
      // Update in state immediately
      dispatch({ type: ORDER_ACTIONS.UPDATE_ORDER, payload: updatedOrder });
      
      // Clear loading state
      dispatch({ type: ORDER_ACTIONS.SET_LOADING, payload: false });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Order updated in state:', updatedOrder);
      }
      
      return updatedOrder;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update order';
      dispatch({ type: ORDER_ACTIONS.SET_ERROR, payload: errorMessage });
      dispatch({ type: ORDER_ACTIONS.SET_LOADING, payload: false });
      throw error;
    }
  }, [clearCache]);

  // Delete order
  const deleteOrder = useCallback(async (orderId) => {
    try {
      dispatch({ type: ORDER_ACTIONS.SET_LOADING, payload: true });
      
      await apiService.orders.delete(orderId);
      
      // Clear cache
      clearCache('orders_data');
      cacheService.clearOrdersData();
      
      // Remove from state
      dispatch({ type: ORDER_ACTIONS.DELETE_ORDER, payload: orderId });
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to delete order';
      dispatch({ type: ORDER_ACTIONS.SET_ERROR, payload: errorMessage });
      throw error;
    }
  }, [clearCache]);

  // Clear error
  const clearError = useCallback(() => {
    dispatch({ type: ORDER_ACTIONS.CLEAR_ERROR });
  }, []);

  // Reset new orders count
  const resetNewOrdersCount = useCallback(() => {
    dispatch({ type: ORDER_ACTIONS.RESET_NEW_ORDERS });
  }, []);

  // Invalidate cache
  const invalidateCache = useCallback(() => {
    clearAllCache();
    cacheService.clearOrdersData();
  }, [clearAllCache]);

  // Reset fetch count and allow fresh API calls
  const resetFetchCount = useCallback(() => {
    fetchCount.current = 0;
    lastFetchTime.current = 0;
    pendingFetch.current = null;
  }, []);

  // Context value
  const contextValue = useMemo(() => ({
    // State
    orders: state.orders,
    loading: state.loading,
    error: state.error,
    lastFetch: state.lastFetch,
    newOrdersCount: state.newOrdersCount,
    
    // Actions
    fetchOrders,
    createOrder,
    updateOrder,
    deleteOrder,
    clearError,
    resetNewOrdersCount,
    invalidateCache,
    resetFetchCount,
    
    // Selectors
    ...selectors
  }), [
    state.orders,
    state.loading,
    state.error,
    state.lastFetch,
    state.newOrdersCount,
    fetchOrders,
    createOrder,
    updateOrder,
    deleteOrder,
    clearError,
    resetNewOrdersCount,
    invalidateCache,
    resetFetchCount,
    selectors
  ]);

  return (
    <OrderContext.Provider value={contextValue}>
      {children}
    </OrderContext.Provider>
  );
};

// Custom hook to use order context
export const useOrders = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrders must be used within an OrderProvider');
  }
  return context;
};

export default OrderContext;
